import React from 'react'
import StatCard from './StatCard.tsx'
import DonutChartCard from './DonutChartCard.tsx'
import PercentChangeBadge from './PercentChangeBadge.tsx'
import PipelineFunnel, { type FunnelColor } from './PipelineFunnel.tsx'
import { formatCurrency } from '../lib/utils.ts'
import { TableHeader, TableContainer } from './Skeleton.tsx'

export interface ReportMetrics {
  period: { start: string; end: string }
  kpis: {
    totalROI: number
    organicROI: number
    paidROI: number
    totalNewRevenue: number
    revenueByChannel: { organic: number; paid: number }
    projectedRevenue: number
    churnRate: number
    lifespan: number
    ltv: number | null
    newMembers: number
    conversionRate: number
    profitPerDollar: number
    projectedROI: number
  }
  totalNewContacts: {
    count: number
    previousCount: number
    percentChange: number
    bySource: { source: string; count: number }[]
  }
  opportunityCountByStage: {
    count: number
    previousCount: number
    percentChange: number
    byStage: { stage: string; count: number }[]
  }
  opportunityByIndividualStage?: {
    pipelineId?: string
    stageName: string
    count: number
    previousCount: number
    percentChange: number
  }[]
  pipelines?: { pipelineId: string; label: string }[]
  leadJourneyFunnel?: {
    available: boolean
    pipelineId?: string
    pipelineLabel?: string
    stages?: { key: string; label: string; count?: number; value?: number }[]
  }
  wonOpportunities: {
    count: number
    previousCount: number
    percentChange: number
    bySource: { source: string; count: number }[]
  }
  currentMembers: {
    count: number
    bySource: { source: string; count: number }[]
  }
  revenue: {
    total: number
    organic: number
    paid: number
    profit: { total: number; organic: number; paid: number }
    costs: { managementFee: number; adSpend: number }
  }
  callAnalytics?: {
    totalCalls: number
    totalInbound: number
    totalFirstTimeCaller: number
    firstTimeCallerRate: number
    totalQualified: number
    qualifiedRate: number
    byStatus: { status: string; count: number }[]
  }
  contactList?: {
    id: string
    firstName: string | null
    lastName: string | null
    phone: string | null
    email: string | null
    source: string
    tags: string | null
    dateAdded: string
    type: string | null
  }[]
}

interface ReportContentProps {
  sections: string[]
  data: ReportMetrics
}

export default function ReportContent({ sections, data }: ReportContentProps) {
  const { kpis } = data
  const showAll = !sections || sections.length === 0
  const showSection = (name: string) => showAll || sections.includes(name)

  return (
    <div className="space-y-8">
      {/* KPI Summary */}
      {showSection('kpis') && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Key Performance Indicators
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard label="Total ROI" value={`${kpis.totalROI}%`} gradient="green" />
            <StatCard
              label="New Revenue"
              value={formatCurrency(kpis.totalNewRevenue)}
              gradient="blue"
            />
            <StatCard
              label="Projected Revenue"
              value={formatCurrency(kpis.projectedRevenue)}
              gradient="purple"
            />
            <StatCard label="Conversion Rate" value={`${kpis.conversionRate}%`} gradient="orange" />
            <StatCard label="New Signups" value={kpis.newMembers} subtitle="Members this period" />
            <StatCard
              label="Organic ROI"
              value={`${kpis.organicROI}%`}
              subtitle="Google Organic / GBP"
            />
            <StatCard
              label="Paid ROI"
              value={`${kpis.paidROI}%`}
              subtitle="Google Guarantee / GLSA"
            />
            <StatCard
              label="Churn Rate"
              value={`${kpis.churnRate}%`}
              subtitle={`Lifespan: ${kpis.lifespan} mo`}
            />
            <StatCard
              label="Lifetime Value"
              value={kpis.ltv != null ? formatCurrency(kpis.ltv) : 'N/A'}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Organic Revenue" value={formatCurrency(data.revenue.organic)} />
            <StatCard label="Paid Revenue" value={formatCurrency(data.revenue.paid)} />
            <StatCard label="Total Profit" value={formatCurrency(data.revenue.profit.total)} />
            <StatCard label="Profit per $1" value={`$${kpis.profitPerDollar}`} />
          </div>
        </section>
      )}

      {/* Lead → Customer Funnel (Lead Journey pipeline) */}
      {showSection('funnel') && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Lead → Customer Funnel
          </h3>
          {data.leadJourneyFunnel?.available && data.leadJourneyFunnel.stages?.length ? (
            <PipelineFunnel
              title={
                data.leadJourneyFunnel.pipelineLabel
                  ? `Lead → Customer Pipeline (${data.leadJourneyFunnel.pipelineLabel})`
                  : 'Lead → Customer Pipeline'
              }
              stages={data.leadJourneyFunnel.stages.map((s, i) => {
                const colors: FunnelColor[] = ['blue', 'amber', 'orange', 'purple', 'emerald']
                return {
                  label: s.label,
                  value: s.key === 'revenue' ? formatCurrency(s.value ?? null) : (s.count ?? 0),
                  color: colors[i % colors.length],
                }
              })}
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
              No Lead Journey pipeline configured — set one in Settings to include this section.
            </div>
          )}
        </section>
      )}

      {/* Lead Analytics */}
      {showSection('leads') && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Lead Analytics
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <DonutChartCard
              title="New Contacts by Source"
              centerValue={data.totalNewContacts.count}
              centerLabel="Total New Contacts"
              percentChange={data.totalNewContacts.percentChange}
              items={data.totalNewContacts.bySource}
            />
            <DonutChartCard
              title="Won Opportunities by Source"
              centerValue={data.wonOpportunities.count}
              centerLabel="Won Deals"
              percentChange={data.wonOpportunities.percentChange}
              items={data.wonOpportunities.bySource}
            />
          </div>
        </section>
      )}

      {/* Opportunities */}
      {showSection('opportunities') && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Opportunities
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <DonutChartCard
              title="Opportunities by Stage"
              centerValue={data.opportunityCountByStage.count}
              centerLabel="Total Opportunities"
              percentChange={data.opportunityCountByStage.percentChange}
              items={data.opportunityCountByStage.byStage.map(s => ({
                label: s.stage,
                count: s.count,
              }))}
            />
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-card">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Stage Breakdown</h3>
              <div className="space-y-3">
                {data.opportunityByIndividualStage?.map(stage => (
                  <div
                    key={`${stage.pipelineId ?? ''}-${stage.stageName}`}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-700">{stage.stageName}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{stage.count}</span>
                      <PercentChangeBadge value={stage.percentChange} size="sm" label="" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Current Members */}
      {showSection('members') && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Current Members
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <StatCard
              label="Total Current Members"
              value={data.currentMembers.count}
              subtitle={`New this period: ${kpis.newMembers}`}
            />
            <DonutChartCard
              title="Members by Source"
              centerValue={data.currentMembers.count}
              centerLabel="Total Members"
              items={data.currentMembers.bySource}
            />
          </div>
        </section>
      )}

      {/* Costs */}
      {showSection('costs') && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Costs
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Management Fee"
              value={formatCurrency(data.revenue.costs.managementFee)}
            />
            <StatCard label="Ad Spend" value={formatCurrency(data.revenue.costs.adSpend)} />
            <StatCard label="Organic Profit" value={formatCurrency(data.revenue.profit.organic)} />
            <StatCard label="Paid Profit" value={formatCurrency(data.revenue.profit.paid)} />
          </div>
        </section>
      )}

      {/* Incoming Calls */}
      {showSection('calls') && data.callAnalytics && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Incoming Calls
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Inbound" value={data.callAnalytics.totalInbound} />
            <StatCard
              label="First-Time Callers"
              value={data.callAnalytics.totalFirstTimeCaller}
              subtitle={`${data.callAnalytics.firstTimeCallerRate}% of inbound`}
            />
            <StatCard
              label="Qualified"
              value={data.callAnalytics.totalQualified}
              subtitle={`${data.callAnalytics.qualifiedRate}% of all calls`}
            />
            <StatCard
              label="Total Calls"
              value={data.callAnalytics.totalCalls}
              subtitle="Inbound + Outbound"
            />
          </div>
          {data.callAnalytics.byStatus.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-card max-w-md">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Inbound Call Status</h4>
              <div className="space-y-2">
                {data.callAnalytics.byStatus.map(s => (
                  <div key={s.status} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 capitalize">
                      {s.status.replace(/-/g, ' ')}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Contact List */}
      {showSection('contacts') && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Contact List{' '}
            {data.contactList && data.contactList.length > 0 && (
              <span className="text-gray-400 font-normal normal-case">
                ({data.contactList.length} contacts{data.contactList.length >= 100 ? '+' : ''})
              </span>
            )}
          </h3>
          <TableContainer
            loading={false}
            hasData={!!(data.contactList && data.contactList.length > 0)}
            skeletonRows={5}
            skeletonCols={6}
            emptyMessage={
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
                No contacts in this period
              </div>
            }
          >
            <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <TableHeader
                    columns={[
                      { label: 'Date', key: 'date', sortable: false },
                      { label: 'Name', key: 'name', sortable: false },
                      { label: 'Phone', key: 'phone', sortable: false },
                      { label: 'Email', key: 'email', sortable: false },
                      { label: 'Source', key: 'source', sortable: false },
                      { label: 'Tags', key: 'tags', sortable: false },
                    ]}
                    sortBy=""
                    sortDir="asc"
                    onSort={() => {}}
                  />
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.contactList?.map(contact => (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {new Date(contact.dateAdded).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {contact.phone || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {contact.email || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {contact.source}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {contact.tags ? (
                            <div className="flex flex-wrap gap-1">
                              {contact.tags
                                .split(',')
                                .map(t => t.trim())
                                .filter(Boolean)
                                .map((tag, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
                                  >
                                    {tag}
                                  </span>
                                ))}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TableContainer>
        </section>
      )}
    </div>
  )
}
